import { mergeMap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GetCookieDTO } from '../data-transfer/entities/get-cookie-dto';
import { environment } from './../../environments/environment';
import { SnackbarService } from './snackbar.service';
import { TranslocoService } from '@jsverse/transloco';
import { LicenseService } from './licensing-service';
import { CoreDataUpdateService } from '../data-transfer/services/core-data-update-api-service';
import { CreditsService } from './credits-service';
import { LoginMethodDto } from '../data-transfer/entities/login-method-dto';
import { DirectoryStateService } from '../components/directory-tree/services/directory-state.service';

export interface UserClaims {
  userId: number;
  userExternalId: string;
  userName: string;
  lastLoginTimestamp: string;
  isTestUser: boolean;
  isUserManager: boolean;
  isAdmin: boolean;
  isGlobalAdmin: boolean;
  isOrganizationLeadAdmin: boolean;
  isOrganizationAdmin: boolean;
  isOrganizationUserManager: boolean;
  isOrganizationPartnershipManager: boolean;
  isValidator: boolean;
  isPremiumUser: boolean;
  isContractSigned: boolean;
  isInactive: boolean;
  isGs1DataExchangeUser: boolean;
}

export enum LoginResponse {
  OK = 0,
  UserUnknown = 1,
  PasswordInvalid = 2,
  OtherError = 3,
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  private userClaims?: UserClaims;
  private stayOnPageHeader = new HttpHeaders({ stayOnPageIfError: 'true' });

  isInitialized = false;
  private initInFlight?: Promise<void>;

  private readonly claimsReloadedSubject = new Subject<void>();
  readonly claimsReloaded$ = this.claimsReloadedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private snackbarService: SnackbarService,
    private translateService: TranslocoService,
    private licenseService: LicenseService,
    private coreDataUpdateService: CoreDataUpdateService,
    private creditService: CreditsService,
    private directoryStateService: DirectoryStateService
  ) { }

  public signInExternal(provider: string, username: string) {
    const returnUrl = encodeURIComponent('/login');
    window.location.href = `${environment.webApiAddress}/api/account/external-login?provider=${provider}&username=${username}&returnUrl=${returnUrl}`;
  }

  public getLoginTypes(login: string) {
    const url = `${environment.webApiAddress}/api/account/login-methods?login=${login}`;
    return this.http.get<LoginMethodDto[]>(url);
  }

  public async signIn(login: string, password: string): Promise<LoginResponse> {
    await this.csrfRefresh();
    const cookieRequestDto = new GetCookieDTO(login, password);
    const url = `${environment.webApiAddress}/api/account/login`;
    let loginResponse: LoginResponse = LoginResponse.OK;
    await this.http.post(url, cookieRequestDto, { headers: this.stayOnPageHeader }).toPromise().catch(
      err => {
        console.log(err);
        switch (err.status) {
          case 404:
            loginResponse = LoginResponse.UserUnknown;
            break;
          case 401:
            loginResponse = LoginResponse.PasswordInvalid;
            break;
          default:
            loginResponse = LoginResponse.OtherError;
            break;
        }
      });
    if (loginResponse === LoginResponse.OK) {
      await this.setUserClaims();
      await this.licenseService.refreshingLicenses();
      await this.coreDataUpdateService.updateCoreData();
      await this.creditService.setCreditsCount();

    }
    await this.csrfRefresh();
    return loginResponse;
  }

  public initSerivce(): Promise<void> {
    if (this.initInFlight) {
      return this.initInFlight;
    }
    this.initInFlight = this.runInit().finally(() => {
      this.initInFlight = undefined;
    });
    return this.initInFlight;
  }

  private async runInit(): Promise<void> {
    const isSignedIn = await this.isSignedIn();
    if (isSignedIn) {
      await Promise.all([
        this.setUserClaims(),
        this.csrfRefresh(),
        this.licenseService.refreshingLicenses(),
        this.coreDataUpdateService.updateCoreData(),
        this.creditService.setCreditsCount(),
      ]);
    }
    this.isInitialized = true;
  }

  public signOut(): Observable<void> {
    return this.http.post(`${environment.webApiAddress}/api/account/logout`, null).pipe(
      mergeMap(_ => this.isSignedIn().then(isSignedIn => {
        if (!isSignedIn) {
          this.clearSession();
        }
      })
    ));
  }

  public csrfRefresh() {
    const url = `${environment.webApiAddress}/api/antiforgery-token`;
    return this.http.get(url, { headers: this.stayOnPageHeader }).toPromise();
  }

  public async hasUserChanged() {
    const userId = this.userClaims?.userId;
    if (userId != null) {
      await this.setUserClaims();
      if (userId !== this.userClaims?.userId) {
        return true;
      }
    }
    return false;
  }

  public async reloadUserClaims(): Promise<void> {
    await Promise.all([this.setUserClaims(), this.csrfRefresh()]);
    this.claimsReloadedSubject.next();
  }

  public clearSession(): void {
    this.userClaims = undefined;
    this.isInitialized = false;
    this.directoryStateService.clearAll();
  }

  private async setUserClaims(): Promise<void> {
    const url = `${environment.webApiAddress}/api/account/user-claims`;
    const result = await this.http.get<UserClaims>(url).toPromise();

    if (result) {
      this.userClaims = result;
    }
  }

  public async isSignedIn(): Promise<boolean | void> {
    const url = `${environment.webApiAddress}/api/account/is-logged-in`;
    return await this.http.get<boolean>(url, { headers: this.stayOnPageHeader }).toPromise().catch(error => {
      if (error.status === 403) {
        this.snackbarService.showInfoWithCloseButton(this.translateService.translate('messages.loginSnackbar'));
      }
    });
  }

  isUserValidator(): boolean {
    if (!this.userClaims) {
      this.throwError('isValidator');
    }
    return this.userClaims?.isValidator;
  }

  isUserAdmin(): boolean {
    if (!this.userClaims) { this.throwError('isAdmin'); }
    return this.userClaims?.isAdmin;
  }

  isOrganizationAdmin(): boolean {
    if (!this.userClaims) { this.throwError('isOrganizationAdmin'); }
    return this.userClaims?.isOrganizationAdmin;
  }

  isLeadAdmin(): boolean {
    if (!this.userClaims) { this.throwError('isOrganizationLeadAdmin'); }
    return this.userClaims?.isOrganizationLeadAdmin;
  }

  isOrganizationUserManager(): boolean {
    if (!this.userClaims) { this.throwError('isOrganizationUserManager'); }
    return this.userClaims?.isOrganizationUserManager;
  }

  isOrganizationPartnershipManager(): boolean {
    if (!this.userClaims) { this.throwError('isOrganizationPartnershipManager'); }
    return this.userClaims?.isOrganizationPartnershipManager;
  }

  canManageOrganization(): boolean {
    return this.hasUserClaims() && this.isOrganizationAdmin();
  }

  hasUserClaims(): boolean {
    return !!this.userClaims;
  }

  IsPremiumUser(): boolean {
    if (!this.userClaims) { this.throwError('isPremiumUser'); }
    return this.userClaims?.isPremiumUser;
  }

  isContractSigned(): boolean {
    if (!this.userClaims) { this.throwError('isContractSigned'); }
    return this.userClaims?.isContractSigned;
  }

  isUserInactive(): boolean {
    if (!this.userClaims) { this.throwError('isInactive'); }
    return this.userClaims?.isInactive;
  }

  isGs1DataTransferUser(): boolean {
    if (!this.userClaims) { this.throwError('isGs1DataExchangeUser'); }
    return this.userClaims?.isGs1DataExchangeUser;
  }

  getSignedInUsername(): string {
    if (!this.userClaims) { this.throwError('userName'); }
    return this.userClaims?.userName;
  }

  getUserId(): number {
    if (!this.userClaims) { this.throwError('userId'); }
    return this.userClaims?.userId;
  }

  getUserExternalId(): string {
    if (!this.userClaims) { this.throwError('userExternalId'); }
    return this.userClaims?.userExternalId;
  }

  getLastLogin(): Date {
    if (!this.userClaims) { this.throwError('lastLoginTimestamp'); }
    return new Date(this.userClaims?.lastLoginTimestamp);
  }

  heartbeat() {
    const url = `${environment.webApiAddress}/api/heartbeat`;
    return this.http.post<null>(url, null, { headers: this.stayOnPageHeader });
  }

  private throwError(fieldName: string): never {
    throw new Error(`AuthService: User not signed in. Field "${fieldName}" cannot be retrieved.`);
  }
}

